import { Grid, Skeleton } from '@mui/material';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton } from '../../../shared/components';
import {
	useDataRoomQuery,
	useDataRoomTermSheetsQuery,
	useActiveDataRoomLendersQuery,
	QueryKeys,
	useExcludedLendersQuery,
} from '../../../shared/hooks';
import { CurrentUser, TermSheet, TermSheetRevision } from '../../../shared/models';
import React from 'react';
import { TermSheetDeleteDialog } from './TermSheetDeleteDialog';
import { TermSheetListItem } from './TermSheetListItem';
import { DecisionForm } from './DecisionForm';
import { AddRevisionForm } from './AddRevisionForm';
import { AddTermSheetForm } from './AddTermSheetForm';
import { useNavigate } from 'react-router-dom';
import { RevisionListDrawer } from '../Revision/RevisionListDrawer';
import { AddExcludedLenderTermSheetForm } from './AddExcludedLenderTermSheetForm';

interface TermSheetPageProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
}

export const TermSheetPage: React.FC<TermSheetPageProps> = ({ dataRoomGuid, currentUser }) => {
	const navigate = useNavigate();
	const dataRoomQuery = useDataRoomQuery(dataRoomGuid);
	const sheetsQuery = useDataRoomTermSheetsQuery(dataRoomGuid);
	const lendersQuery = useActiveDataRoomLendersQuery(dataRoomGuid);
	const excludedLendersQuery = useExcludedLendersQuery(dataRoomQuery.data?.dealGuid, dataRoomQuery.isSuccess);

	const [isAddingTermSheet, setIsAddingTermSheet] = React.useState<boolean>(false);
	const [isAddingExcludedLenderTermSheet, setIsAddingExcludedLenderTermSheet] = React.useState<boolean>(false);
	const [feebackBeingEdited, setFeedbackBeingEdited] = React.useState<TermSheetRevision | null>(null);
	const [newRevisionBeingCreated, setNewRevisionBeingCreated] = React.useState<TermSheetRevision | null>(null);
	const [termSheetBeingDeleted, setTermSheetBeingDeleted] = React.useState<TermSheet | null>(null);
	const [revisionList, setRevisionList] = React.useState<TermSheet | null>(null);

	const onEditTermSheetRevisionClicked = (revision: TermSheetRevision | null): void => {
		navigate(`/${dataRoomGuid}/TermSheets/${revision?.termSheetGuid}/Revisions/${revision?.guid}`);
	};

	const clearDialogsAndDrawers = () => {
		setTermSheetBeingDeleted(null);
		setNewRevisionBeingCreated(null);
		setFeedbackBeingEdited(null);
		setIsAddingTermSheet(false);
		setRevisionList(null);
		setIsAddingExcludedLenderTermSheet(false);
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Term Sheets</CerebroBreadcrumb>

			{sheetsQuery.isLoading && lendersQuery.isLoading && dataRoomQuery.isLoading && (
				<>
					<Skeleton height={200} />
					<Skeleton height={200} />
					<Skeleton height={200} />
				</>
			)}

			{sheetsQuery.isSuccess &&
				dataRoomQuery.isSuccess &&
				lendersQuery.isSuccess &&
				excludedLendersQuery.isSuccess && (
					<>
						<CerebroSecondaryButton onClick={() => setIsAddingTermSheet(true)} sx={{ marginBottom: 5 }}>
							Upload Term Sheet
						</CerebroSecondaryButton>

						<CerebroSecondaryButton
							onClick={() => setIsAddingExcludedLenderTermSheet(true)}
							sx={{ marginBottom: 5, marginLeft: 2 }}
						>
							Upload Excluded Lender Term Sheet
						</CerebroSecondaryButton>
					</>
				)}

			{sheetsQuery.isSuccess && sheetsQuery.data.length > 0 && (
				<Grid container spacing={2} alignItems="stretch">
					{sheetsQuery.data?.map(sheet => {
						const currentRevision = sheet.revisions.find(s => s.isCurrentVersion) ?? null;
						const isVisible = currentUser.isEmployeeUser || sheet.visibleToBorrower;

						return isVisible ? (
							<Grid key={sheet.guid} item xs={12}>
								<TermSheetListItem
									dataRoomGuid={dataRoomGuid}
									sheet={sheet}
									currentRevision={currentRevision}
									currentUser={currentUser}
									enterFeedback={setFeedbackBeingEdited}
									editRevision={onEditTermSheetRevisionClicked}
									newRevision={setNewRevisionBeingCreated}
									deleteTermSheet={setTermSheetBeingDeleted}
									revisionList={setRevisionList}
								/>
							</Grid>
						) : (
							<></>
						);
					})}
				</Grid>
			)}

			{lendersQuery.isSuccess && (
				<AddTermSheetForm
					dataRoomGuid={dataRoomGuid}
					currentUser={currentUser}
					availableLenders={lendersQuery.data}
					isAdding={isAddingTermSheet}
					close={clearDialogsAndDrawers}
				/>
			)}

			{excludedLendersQuery.isSuccess && (
				<AddExcludedLenderTermSheetForm
					dataRoomGuid={dataRoomGuid}
					currentUser={currentUser}
					availableLenders={excludedLendersQuery.data}
					isAdding={isAddingExcludedLenderTermSheet}
					close={clearDialogsAndDrawers}
				/>
			)}

			<DecisionForm
				dataRoomGuid={dataRoomGuid}
				currentUser={currentUser}
				revision={feebackBeingEdited}
				close={clearDialogsAndDrawers}
			/>
			<AddRevisionForm
				dataRoomGuid={dataRoomGuid}
				currentUser={currentUser}
				revision={newRevisionBeingCreated}
				close={clearDialogsAndDrawers}
			/>
			<TermSheetDeleteDialog
				dataRoomGuid={dataRoomGuid}
				sheet={termSheetBeingDeleted}
				close={clearDialogsAndDrawers}
			/>
			<RevisionListDrawer
				dataRoomGuid={dataRoomGuid}
				sheet={revisionList}
				editRevision={onEditTermSheetRevisionClicked}
				close={clearDialogsAndDrawers}
			/>
		</CerebroPageContainer>
	);
};
